import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2 } from 'lucide-react';
import { AnimatedBackground } from '@/components/ui/AnimatedBackground';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (!isLoading && isAuthenticated && requiredRole && !hasRole(requiredRole)) {
      navigate('/unauthorized', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, requiredRole, hasRole, user]);

  // Si ya estamos autenticados, renderizar el children directamente sin mostrar la pantalla de verificando
  if (isAuthenticated) {
    if (requiredRole && !hasRole(requiredRole)) {
      return null;
    }
    return <>{children}</>;
  }

  // Solo mostramos la pantalla de verificando si está cargando y no sabemos si está autenticado
  if (isLoading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center overflow-hidden">
        <AnimatedBackground />
        <div className="text-center z-10">
          <Loader2 className="h-8 w-8 animate-spin text-[#312e81] mx-auto mb-4" />
          <p className="text-slate-600 font-semibold">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  return null;
}